import mvtTextbox from '@/common/components/mvttextbox/mvt-textbox.vue';
import mvtSocial from '@/common/components/mvtsocial/mvt-social.vue';
import { mapActions, mapGetters } from 'vuex';
import { getStorage, setStorage } from '@/common/utilities/nojq/_localstorage.js';

const WAITING = 60;
const OTP_RESEND_STORAGE_KEY = 'otpResend';

function isValidPasscode(value) {
    return /^\d{6}$/.test(String(value || '').trim());
}

function normalizeEmail(email) {
    return String(email || '')
        .trim()
        .toLowerCase();
}

function readOtpResend() {
    return getStorage(OTP_RESEND_STORAGE_KEY) || {};
}

function writeOtpResend(email) {
    const normalized = normalizeEmail(email);
    if (!normalized) {
        setStorage(OTP_RESEND_STORAGE_KEY, null);
        return;
    }
    setStorage(OTP_RESEND_STORAGE_KEY, { email: normalized, time: Date.now() });
}

export default {
    name: 'mvtEmailLogin',
    components: {
        mvtTextbox,
        mvtSocial,
    },
    props: {
        options: {
            type: Object,
            default: {
                trigger: '',
                errorMessage: '',
                email: '',
                isUserExist: false,
                activated: undefined,
            },
        },
    },
    inject: ['$eventBus'],
    data() {
        return {
            errorMsg: '',
            formState: '',
            username: (this.options && this.options.email) || '',
            password: '',
            showPassword: false,
            phone: (this.options && this.options.phone) || '',
            name: (this.options && this.options.name) || '',
            loginMode: 'password',
            localPasscode: '',
            passcodeFocused: false,
            sendingCode: false,
            resendCountdown: 0,
            resendTimerId: null,
        };
    },
    computed: {
        ...mapGetters('glb', ['glb', 'getErrorHtml', 'getSplit']),
        isUserExist() {
            return this.options.isUserExist;
        },
        isActivated() {
            return this.options.activated === true;
        },
        showCodeLogin() {
            return this.isUserExist && this.loginMode === 'code';
        },
        showResendCode() {
            return !this.isActivated;
        },
        canResend() {
            return this.resendCountdown <= 0;
        },
        canContinueWithCode() {
            return isValidPasscode(this.localPasscode) && !this.sendingCode;
        },
    },
    mounted() {
        $(this.$el).on('focus', 'input', () => {
            this.errorMsg = '';
        });
        this.restoreResendCountdown();
        if (this.isUserExist && this.options.activated === false) {
            this.onSendCode();
        }
    },
    beforeUnmount() {
        this.clearResendTimer();
    },
    methods: {
        ...mapActions('glb', ['login', 'sendOtp']),
        switchComponent: function (name) {
            this.$emit('switchComponent', name);
        },
        onBack: function () {
            if (this.options && this.options.type === 'mvtWelcomeBack') {
                this.switchComponent('mvtWelcomeBack');
            } else {
                this.switchComponent('mvtLogin');
            }
        },
        toggleShowPassword() {
            this.showPassword = !this.showPassword;
            if (this.showPassword) {
                this.$refs.password.type = 'text';
            } else {
                this.$refs.password.type = 'password';
            }
        },
        clearResendTimer() {
            if (this.resendTimerId) {
                clearInterval(this.resendTimerId);
                this.resendTimerId = null;
            }
        },
        startResendCountdown(seconds) {
            this.clearResendTimer();
            this.resendCountdown = Math.max(0, Number(seconds) || 0);
            if (this.resendCountdown <= 0) {
                return;
            }
            this.resendTimerId = setInterval(() => {
                if (this.resendCountdown <= 1) {
                    this.resendCountdown = 0;
                    this.clearResendTimer();
                    return;
                }
                this.resendCountdown -= 1;
            }, 1000);
        },
        /** Restore local countdown from localStorage sent-at; only for the same email. */
        restoreResendCountdown() {
            const email = normalizeEmail(this.username);
            const stored = readOtpResend();
            if (email && email === stored.email && stored.time) {
                const remainingMs = stored.time + WAITING * 1000 - Date.now();
                const remaining = Math.ceil(remainingMs / 1000);
                this.resendCountdown = remaining > 0 ? remaining : 0;
                if (this.resendCountdown > 0) {
                    this.startResendCountdown(this.resendCountdown);
                }
            } else {
                this.resendCountdown = 0;
            }
        },
        focusPasscodeInput() {
            this.$nextTick(() => {
                this.$refs.passcodeInput && this.$refs.passcodeInput.focus();
                $(document).trigger('dom.resize');
            });
        },
        async requestEmailCode() {
            this.sendingCode = true;
            this.errorMsg = '';
            try {
                const resp = await this.sendOtp({
                    email: this.username,
                    redirectUri: window.location.pathname,
                });
                const status = resp && resp.status;
                if (status && status.code === 0) {
                    const seconds = resp.data && resp.data.resendEmailInSeconds;
                    writeOtpResend(this.username);
                    this.startResendCountdown(seconds || WAITING);
                } else {
                    this.errorMsg = this.getErrorHtml(status);
                }
            } catch (err) {
                this.errorMsg = this.getErrorHtml(err && err.status);
            } finally {
                this.sendingCode = false;
            }
        },
        onSendCode() {
            this.loginMode = 'code';
            this.localPasscode = '';
            this.errorMsg = '';
            this.formState = '';
            if (this.resendCountdown <= 0) {
                this.requestEmailCode();
            }
            setTimeout(() => {
                this.focusPasscodeInput();
            }, 100);
        },
        onUsePassword() {
            this.loginMode = 'password';
            this.localPasscode = '';
            this.errorMsg = '';
            this.formState = '';
        },
        async onResendCode() {
            if (!this.canResend || this.sendingCode) {
                return;
            }
            this.localPasscode = '';
            this.errorMsg = '';
            await this.requestEmailCode();
            this.focusPasscodeInput();
        },
        onPasscodeInput(event) {
            const digits = String(event.target.value || '')
                .replace(/\D/g, '')
                .slice(0, 6);
            this.localPasscode = digits;
            event.target.value = digits;
            if (digits.length === 6) {
                event.target.blur();
            }
        },
        submitCode() {
            if (!this.canContinueWithCode) {
                return;
            }
            this.formState = 'submitting';
            this.login({
                username: this.username,
                otp: this.localPasscode,
                trigger: this.options.trigger || this.$options.name,
                userType: this.glb.pageInfo.userType,
            })
                .then((resp) => {
                    let status = resp && resp.status;
                    if (status && status.code === 0) {
                        this.errorMsg = '';
                        this.formState = 'submitted';
                        this.$eventBus.$emit('eventbus-authenticated', this.options.requestName);
                        this.$eventBus.$emit('close-dialog', this.$options.name);
                    } else {
                        this.formState = 'rejected';
                        this.errorMsg = this.getErrorHtml(status);
                    }
                })
                .catch(() => {
                    this.formState = '';
                });
        },
        submit: function () {
            if (this.showCodeLogin) {
                this.submitCode();
                return;
            }
            this.formState = 'submitting';
            this.login({
                username: this.username,
                password: this.password,
                trigger: this.options.trigger || this.$options.name,
                userType: this.glb.pageInfo.userType,
                phone: this.phone,
                name: this.name,
            })
                .then((resp) => {
                    let status = resp && resp.status;
                    if (status && status.code === 0) {
                        this.errorMsg = '';
                        this.formState = 'submitted';
                        this.$eventBus.$emit('eventbus-authenticated', this.options.requestName);
                        this.$eventBus.$emit('close-dialog', this.$options.name);
                    } else {
                        this.formState = 'rejected';
                        this.errorMsg = this.getErrorHtml(status);
                    }
                })
                .catch((err) => {
                    this.formState = '';
                });
        },
    },
};
